import { FocusMonitor } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ChatStore, PromptGeneratorService } from '@coin/chat-bot-data-access';
import {
  ChatDialogContentComponent,
  ChatDialogFooterComponent,
  ChatDialogHeaderComponent,
  ChatMessageComponent,
  ChatStartViewComponent
} from '@coin/chat-bot-ui';
import { ReferenceDocument, CocoCelebrationService } from '@coin/chat-bot-util-types';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { TranslateModule } from '@ngx-translate/core';
import { filter, fromEvent, switchMap } from 'rxjs';
import { ChatFaqScreenComponent } from '../chat-faq-screen/chat-faq-screen.component';
import { CdkDrag, CdkDragHandle, DragRef, Point } from '@angular/cdk/drag-drop';
import { ResizableDirective } from '../../directives/resizable/resizable.directive';
import { MatIcon } from '@angular/material/icon';
import { CelebrationAnimationComponent } from '../celebration-animation/celebration-animation.component';

@Component({
  selector: 'coin-chat-dialog',
  templateUrl: './chat-dialog.component.html',
  styleUrl: './chat-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChatDialogHeaderComponent,
    ChatDialogContentComponent,
    ChatDialogFooterComponent,
    ChatMessageComponent,
    ChatStartViewComponent,
    TranslateModule,
    ChatFaqScreenComponent,
    CdkDrag,
    CdkDragHandle,
    ResizableDirective,
    MatIcon,
    CelebrationAnimationComponent
  ],
  providers: [PromptGeneratorService],
  host: {
    '(keydown)': 'handleKeydown($event)'
  }
})
export class ChatDialogComponent {
  private readonly chatBox = viewChild<ElementRef<HTMLDivElement>>('chatBox');
  private readonly chatStore = inject(ChatStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elRef = inject(ElementRef);
  private readonly promptGeneratorService = inject(PromptGeneratorService);
  private readonly dialog = inject(MatDialog);
  private readonly celebrationManager = inject(CocoCelebrationService);

  messages = this.chatStore.selectSignal(state => state._messages);
  isLoadingAssistantResponse = this.chatStore.selectSignal(state => state._isLoading);
  isActive = true;
  user = this.chatStore.user;
  isSendButtonDisabled = computed(() => this.chatStore.isSendDisabled());
  isFaqScreenActive = signal(false);
  hasPendingMessages = computed<boolean>(() => {
    return this.messages().some(message => !message.finished);
  });
  prompts = this.promptGeneratorService.prompts;

  protected readonly celebrationType = signal<string | null>(this.celebrationManager.getType());
  protected readonly hasMessages = computed(() => this.messages().length > 0);

  constructor() {
    // Monitor keyboard
    fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.handleKeydown(event));

    // Monitor focus
    this.focusMonitor
      .monitor(this.elRef.nativeElement, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(origin => {
        this.isActive = !!origin;
      });

    // Monitor click inside chat box
    toObservable(this.chatBox)
      .pipe(
        filter(Boolean),
        switchMap(chatBox => fromEvent(chatBox.nativeElement, 'click')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isActive = true;
      });
  }

  constrainPosition = (
    userPointerPosition: Point,
    _dragRef: DragRef,
    dimensions: DOMRect,
    pickupPositionInElement: Point
  ) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate top-left position of the dialog based on cursor offset
    let newX = userPointerPosition.x - pickupPositionInElement.x;
    let newY = userPointerPosition.y - pickupPositionInElement.y;

    // Clamp horizontally
    if (newX < 0) newX = 0;
    else if (newX + dimensions.width > viewportWidth) newX = viewportWidth - dimensions.width;

    // Clamp vertically
    if (newY < 0) newY = 0;
    else if (newY + dimensions.height > viewportHeight) newY = viewportHeight - dimensions.height;

    return { x: newX, y: newY };
  };

  closeChat() {
    this.chatStore.toggle();
  }

  sendMessage(content: string) {
    this.chatStore.postUserMessage(content);
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isActive && this.isChatActive() && this.chatStore.isOpen()) {
      event.preventDefault();
      this.closeChat();
    }
  }

  private isChatActive(): boolean {
    const focusedEl = document.activeElement;
    return this.chatBox()?.nativeElement.contains(focusedEl) || false;
  }

  downloadFile(document: ReferenceDocument) {
    this.chatStore.downloadFile(document);
  }

  cleanMessages() {
    ConfirmationDialogComponent.openDialog(this.dialog, {
      headline: 'general.confirm',
      translate: true,
      msg: `chat-bot.window.delete-context-message`,
      confirmMsg: 'general.delete',
      cancelMsg: 'general.btnCancel'
    })
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.chatStore.clearMessages();
      });
  }

  toggleFaq() {
    this.isFaqScreenActive.update(value => !value);
  }
}
